import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import RichEditor from '../../components/common/RichEditor';
import { getAdminSolution, createSolution, updateSolution } from '../../api/content';
import { fetchWithAuth } from '../../api/apiClient';
import AdminButton from '../../components/admin/AdminButton';
import AiAssistantModal from '../../components/admin/ai/AiAssistantModal';
import AiImageGeneratorModal from '../../components/admin/common/AiImageGeneratorModal';
import ContentCategoryManager from '../../components/admin/ContentCategoryManager';
import { getContentCategories } from '../../api/contentCategories';
import { ACCEPT_IMAGE, ACCEPT_PDF, IMAGE_HINT, PDF_HINT } from '../../constants/uploads';
import { BASE_URL } from '../../api/inquiries';

/**
 * 대표 포함 최대 3개. 서버(MAX_CATEGORIES_PER_SOLUTION)와 같은 값이다.
 *
 * 분류의 가치는 무엇을 포함하느냐가 아니라 무엇을 제외하느냐에서 나온다.
 * 전부 체크할 수 있게 두면 필터가 늘 전체를 반환해 분류가 죽는다.
 */
const MAX_CATEGORIES = 3;

/** AI 보조작가가 제안하고 사람이 고를 필드. 순서가 검토 화면의 순서다. */
const SOLUTION_AI_FIELDS = [
    { key: 'title', label: '제목' },
    { key: 'subtitle', label: '부제' },
    { key: 'category', label: '카테고리' },
    { key: 'content', label: '본문' },
];

const SolutionEditor = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const editorRef = useRef(null);
    const isEditMode = !!id && id !== 'new';

    const [loading, setLoading] = useState(isEditMode);
    const [saving, setSaving] = useState(false);
    const [formData, setFormData] = useState({
        title: '',
        subtitle: '',
        slug: '',
        // 대표가 맨 앞. 하나의 솔루션이 여러 업종에서 쓰이는 것은 예외가 아니라 정상이다.
        categoryIds: [],
        thumbnail: '',
        isPublic: true,
        manualFile: '',
        proposalFile: '',
    });
    
    // 카테고리 (cms.categories)
    const [categoryOptions, setCategoryOptions] = useState([]);
    const [categoryManagerOpen, setCategoryManagerOpen] = useState(false);

    const loadCategories = useCallback(async () => {
        try {
            setCategoryOptions(await getContentCategories('solution'));
        } catch (err) {
            // 카테고리를 못 불러와도 편집 자체는 계속할 수 있어야 한다.
            console.error('카테고리 로드 실패', err);
        }
    }, []);

    useEffect(() => { loadCategories(); }, [loadCategories]);

    // AI Modal State
    const [isAiModalOpen, setIsAiModalOpen] = useState(false);
    const [isAiImageModalOpen, setIsAiImageModalOpen] = useState(false);
    
    // 썸네일 이미지 처리 — AI 보조작가 모달에서 분리해 왔다.
    const [thumbProcessing, setThumbProcessing] = useState(false);

    const processThumbnail = async (mode) => {
        if (!formData.thumbnail) return;
        setThumbProcessing(true);
        try {
            const endpoint = mode === 'removeBg' ? 'process-image' : 'batch-crop-images';
            const payload = mode === 'removeBg'
                ? { imageUrl: formData.thumbnail }
                : { imageUrls: [formData.thumbnail] };

            const res = await fetchWithAuth(`${BASE_URL}/api/ai/${endpoint}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            if (!res.ok) throw new Error('처리 실패');
            const data = await res.json();
            const url = mode === 'removeBg' ? data.url : data.urls?.[0];
            if (!data.success || !url) throw new Error('처리 결과를 받지 못했습니다.');

            setFormData(prev => ({ ...prev, thumbnail: url }));
        } catch (err) {
            console.error('썸네일 처리 실패', err);
            alert('이미지 처리에 실패했습니다.');
        } finally {
            setThumbProcessing(false);
        }
    };

    const handleAiImageInsert = (url) => {
        setFormData(prev => ({ ...prev, thumbnail: url }));
        alert('AI 썸네일이 성공적으로 반영되었습니다.');
    };

    /**
     * AI 보조작가가 "선택 반영"한 필드만 받는다.
     * 이전에는 생성 결과가 폼을 통째로 덮어써서 undo 스냅샷과 덮어쓰기 confirm 이
     * 필요했다. 지금은 모달에서 사람이 필드별로 고른 뒤 넘어오므로 둘 다 없앴다.
     */
    const handleAiApply = (patch) => {
        const { content, ...rest } = patch;
        // AI 에게는 사람이 읽는 이름(label)으로 물었으므로 답도 label 로 온다.
        // 저장은 id 로 하니 여기서 되돌린다. AI 가 정하는 것은 **대표 하나뿐**이고,
        // 추가 카테고리는 사람이 고른 것을 유지한다 — 어느 업종에 따로 할 말이 있는지는
        // AI 가 판단할 수 있는 종류의 문제가 아니다.
        if (rest.category !== undefined) {
            const hit = categoryOptions.find((c) => c.label === rest.category);
            delete rest.category;
            if (hit) {
                setFormData((prev) => ({
                    ...prev,
                    categoryIds: [hit.id, ...prev.categoryIds.filter((cid) => cid !== hit.id)]
                        .slice(0, MAX_CATEGORIES),
                }));
            }
        }
        if (Object.keys(rest).length > 0) {
            setFormData(prev => ({ ...prev, ...rest }));
        }
        if (content !== undefined && editorRef.current) {
            editorRef.current.setHTML(content);
        }
    };

    useEffect(() => {
        if (isEditMode) {
            loadSolution();
        }
    }, [id]);

    const loadSolution = async () => {
        try {
            const data = await getAdminSolution(id);
            setFormData({
                title: data.title || '',
                subtitle: data.subtitle || '',
                slug: data.slug || '',
                categoryIds: (data.categories || []).map((c) => c.id),
                thumbnail: data.thumbnail || '',
                isPublic: data.isPublic ?? true,
                manualFile: data.manualFile || '',
                proposalFile: data.proposalFile || '',
            });

            // Editor content 로딩 보장
            if (data.content) {
                const timer = setTimeout(() => {
                    if (editorRef.current) {
                        editorRef.current.setHTML(data.content);
                    }
                }, 200);
                return () => clearTimeout(timer);
            }
        } catch (error) {
            console.error('Failed to load solution', error);
            alert('솔루션을 불러올 수 없습니다.');
            navigate('/admin/solutions');
        } finally {
            setLoading(false);
        }
    };

    const primaryCategory = categoryOptions.find((c) => c.id === formData.categoryIds[0]) || null;

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleFileUpload = async (e, fieldName) => {
        const file = e.target.files[0];
        if (!file) return;

        if (fieldName === 'manualFile' || fieldName === 'proposalFile') {
            // html/htm 은 더 이상 받지 않는다 — 업로드물은 /uploads 에서 같은 오리진으로
            // 서빙되어 저장형 XSS 통로가 된다. 실사용 0건이라 제거해도 잃는 게 없다.
            const fileType = file.name.split('.').pop().toLowerCase();
            if (fileType !== 'pdf') {
                alert('PDF 파일만 업로드할 수 있습니다.');
                return;
            }
        }

        const uploadData = new FormData();
        uploadData.append('file', file);

        try {
            const res = await fetchWithAuth(`${BASE_URL}/api/upload?folder=tmp`, {
                method: 'POST',
                body: uploadData
            });

            if (!res.ok) throw new Error('Upload failed');
            const data = await res.json();
            setFormData(prev => ({ ...prev, [fieldName]: data.url || data.path }));
        } catch (error) {
            console.error(`${fieldName} upload failed`, error);
            alert('파일 업로드 실패');
        }
    };

    const handleDeleteFile = async (fieldName) => {
        // 1차: 기본 삭제 확인
        if (!window.confirm('정말 삭제하시겠습니까? 서버상의 파일도 즉시 삭제됩니다.')) {
            return;
        }

        // 2차: 썸네일일 경우 추가 경고 (Double Check)
        if (fieldName === 'thumbnail') {
            if (!window.confirm('썸네일을 삭제하면 목록에 노출되지 않습니다.\n그래도 삭제하시겠습니까?')) {
                return;
            }
        }

        const filePath = formData[fieldName];
        if (filePath) {
            try {
                // Remove /uploads prefix if needed or just pass as is (controller expects path)
                // Our backend logic assumes full path or relative path?
                // Controller join(process.cwd(), filePath).
                // API saves '/uploads/misc/file.png'.
                // So passing '/uploads/misc/file.png' works.

                await fetchWithAuth(`${BASE_URL}/api/upload?folder=tmp`, {
                    method: 'DELETE',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ path: filePath })
                });
            } catch (error) {
                console.error('File delete warning', error);
                // We proceed to clear referencing field even if delete fails (e.g. file already gone)
            }
        }
        setFormData(prev => ({ ...prev, [fieldName]: '' }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);

        try {
            const content = editorRef.current?.getHTML() || '';
            const payload = { ...formData, content };

            if (isEditMode) {
                await updateSolution(id, payload);
                alert('수정되었습니다.');
            } else {
                await createSolution(payload);
                alert('등록되었습니다.');
            }
            navigate('/admin/solutions');
        } catch (error) {
            console.error('Failed to save', error);
            alert('저장에 실패했습니다.');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <AdminLayout>
                <div className="p-10 text-center text-gray-400 font-bold animate-pulse">로딩 중...</div>
            </AdminLayout>
        );
    }

    return (
        <AdminLayout>
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
                <div className="flex items-center gap-4">
                    <AdminButton variant="secondary" size="sm" onClick={() => navigate('/admin/solutions')} className="!px-3">&larr;</AdminButton>
                    <div className="flex items-center gap-3">
                        <h2 className="text-2xl font-extrabold text-gray-800 m-0 tracking-tight">{isEditMode ? '솔루션 상세 수정' : '새 솔루션 등록'}</h2>
                        {/* '되돌리기' 버튼을 없앴다 — AI 결과를 폼에 바로 쓰지 않고
                            검토 화면에서 필드별로 골라 반영하므로 되돌릴 일이 없다. */}
                        <AdminButton 
                            variant="primary" 
                            size="sm" 
                            onClick={() => setIsAiModalOpen(true)}
                            className="!bg-gradient-to-r from-primary-600 to-pink-500 border-0 !px-3 shadow-md hover:shadow-lg transition-transform hover:-translate-y-0.5"
                        >
                            ✨ AI 보조작가
                        </AdminButton>
                    </div>
                </div>
            </div>

            <div className="bg-white p-6 md:p-8 rounded-2xl shadow-sm border border-gray-200 max-w-5xl">
                <form onSubmit={handleSubmit} className="space-y-6">
                    {/* 제목 및 카테고리 */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                            <label className="block text-sm font-bold text-gray-700">제목 *</label>
                            <input
                                type="text"
                                name="title"
                                value={formData.title}
                                onChange={handleChange}
                                required
                                className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none shadow-sm text-sm font-bold text-gray-900"
                                placeholder="솔루션 제목"
                            />
                        </div>
                        <div className="space-y-2">
                            <div className="flex items-center justify-between">
                                <label htmlFor="solution-category" className="block text-sm font-bold text-gray-700">카테고리</label>
                                <button
                                    type="button"
                                    onClick={() => setCategoryManagerOpen(true)}
                                    className="text-xs font-bold text-gray-500 hover:text-primary-500"
                                >
                                    카테고리 관리
                                </button>
                            </div>
                            {/*
                              자유 입력 → 단일 선택 → 다중 선택으로 두 번 바뀌었다.

                              단일이 실패한 근거는 '범용솔루션' 3건이다. 업종을 하나만
                              고르라니까 고를 수가 없어서 만들어진 탈출구였다. 하나의
                              솔루션이 여러 업종에서 쓰이는 것은 예외가 아니라 정상이다.

                              그래도 전부 동등하게 두지는 않는다. 필터·랜딩페이지는 다중이
                              맞지만 카드 배지와 정렬은 하나여야 해서 대표를 지정한다.
                              저장은 id 로 한다 — 이름은 언제든 바뀌므로 키가 될 수 없다.
                            */}
                            <select
                                id="solution-category"
                                name="category"
                                value={formData.categoryIds[0] ?? ''}
                                onChange={(e) => {
                                    const next = e.target.value ? Number(e.target.value) : null;
                                    setFormData((prev) => {
                                        const rest = prev.categoryIds.slice(1).filter((cid) => cid !== next);
                                        return { ...prev, categoryIds: next ? [next, ...rest] : rest };
                                    });
                                }}
                                className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none shadow-sm text-sm bg-white"
                            >
                                <option value="">대표 카테고리를 고르세요</option>
                                {categoryOptions.map((c) => (
                                    <option key={c.id} value={c.id}>{c.label}</option>
                                ))}
                            </select>

                            {/* 추가 카테고리 — 필터와 업종별 랜딩 페이지에만 반영된다. */}
                            <div className="mt-3">
                                <div className="flex items-baseline justify-between mb-1.5">
                                    <span className="text-xs font-bold text-gray-700">
                                        추가 업종 <span className="font-normal text-gray-400">(선택)</span>
                                    </span>
                                    <span className={`text-[11px] font-bold ${formData.categoryIds.length >= MAX_CATEGORIES ? 'text-primary-500' : 'text-gray-400'}`}>
                                        {formData.categoryIds.length} / {MAX_CATEGORIES}
                                    </span>
                                </div>
                                <div className="flex flex-wrap gap-1.5">
                                    {categoryOptions
                                        .filter((c) => c.id !== formData.categoryIds[0])
                                        .map((c) => {
                                            const on = formData.categoryIds.includes(c.id);
                                            const full = !on && formData.categoryIds.length >= MAX_CATEGORIES;
                                            return (
                                                <button
                                                    key={c.id}
                                                    type="button"
                                                    disabled={full || !formData.categoryIds.length}
                                                    onClick={() => setFormData((prev) => ({
                                                        ...prev,
                                                        categoryIds: on
                                                            ? prev.categoryIds.filter((cid) => cid !== c.id)
                                                            : [...prev.categoryIds, c.id],
                                                    }))}
                                                    className={`px-3 py-1.5 rounded-lg text-xs font-bold border transition-colors ${
                                                        on
                                                            ? 'bg-primary-50 border-primary-500 text-primary-600'
                                                            : 'bg-white border-gray-300 text-gray-600 hover:border-gray-400 disabled:opacity-40 disabled:hover:border-gray-300'
                                                    }`}
                                                >
                                                    {on ? '✓ ' : ''}{c.label}
                                                </button>
                                            );
                                        })}
                                </div>
                                <p className="mt-2 text-[11px] text-gray-500 leading-relaxed">
                                    기준은 &lsquo;쓸 수 있는가&rsquo;가 아니라 <b className="text-gray-700">이 업종 고객에게 따로 설명할 내용이 있는가</b>입니다.
                                    해당 업종 페이지에 실렸을 때 말이 되는 것만 고르세요.
                                </p>
                            </div>

                            {/*
                              주소(slug). 대표 카테고리 코드와 합쳐져 정본 경로가 된다 — 03 §2-6.
                              추가 업종은 주소에 들어가지 않는다. 들어가면 같은 내용이 여러 주소에
                              생겨 검색엔진이 정본을 임의로 고르게 된다.
                            */}
                            <div className="mt-4">
                                <label htmlFor="solution-slug" className="block text-xs font-bold text-gray-700 mb-1.5">
                                    주소 <span className="font-normal text-gray-400">(URL · 공개 후 변경 금지)</span>
                                </label>
                                <input
                                    id="solution-slug"
                                    name="slug"
                                    value={formData.slug}
                                    onChange={handleChange}
                                    placeholder="비워 두면 제목에서 자동으로 만듭니다"
                                    className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none shadow-sm text-sm font-mono"
                                />
                                <p className="mt-1.5 text-[11px] text-gray-500 break-all">
                                    {primaryCategory && formData.slug
                                        ? <>공개 주소: <b className="text-gray-700">/solutions/{primaryCategory.slug}/{formData.slug}</b></>
                                        : '대표 카테고리와 주소를 정하면 공개 주소가 여기 표시됩니다.'}
                                </p>
                                <p className="mt-1 text-[11px] text-gray-500 leading-relaxed">
                                    공개한 뒤 주소를 바꾸거나 <b className="text-gray-700">대표 카테고리를 옮기면</b> 경로가 달라집니다.
                                    이때 옛 주소는 자동으로 새 주소로 넘어가도록(301) 등록되지만, 검색 순위가 옮겨가는 데는 시간이 걸립니다.
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* 부제목 */}
                    <div className="space-y-2">
                        <label className="block text-sm font-bold text-gray-700">부제목</label>
                        <input
                            type="text"
                            name="subtitle"
                            value={formData.subtitle}
                            onChange={handleChange}
                            className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none shadow-sm text-sm"
                            placeholder="간단한 설명"
                        />
                    </div>

                    {/* 파일 업로드 섹션 */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 bg-gray-50 p-5 rounded-xl border border-gray-200">
                        <div className="space-y-2">
                            <label className="block text-sm font-bold text-gray-700">썸네일 이미지</label>
                            <div className="flex flex-col xl:flex-row gap-2 items-start xl:items-center">
                                <input type="file" onChange={(e) => handleFileUpload(e, 'thumbnail')} className="block w-full text-xs text-gray-500 file:mr-2 file:py-1 file:px-3 file:rounded-md file:border-0 file:text-xs file:font-bold file:bg-white file:border-gray-200 file:text-gray-700 hover:file:bg-gray-50 cursor-pointer" accept={ACCEPT_IMAGE} />
                                <div className="flex items-center gap-2">
                                    <AdminButton type="button" variant="primary" size="sm" onClick={() => setIsAiImageModalOpen(true)} className="!bg-gradient-to-r from-blue-600 to-indigo-500 border-0 shadow-sm shrink-0 !py-1 !px-2 text-[11px] whitespace-nowrap hidden sm:block">
                                        🎨 AI 썸네일
                                    </AdminButton>
                                    {formData.thumbnail && (
                                        <button
                                            type="button"
                                            onClick={() => handleDeleteFile('thumbnail')}
                                            className="w-8 h-8 flex items-center justify-center shrink-0 rounded-md bg-red-50 text-red-600 hover:bg-red-100 transition-colors shadow-sm font-bold"
                                            title="삭제"
                                        >
                                            ✕
                                        </button>
                                    )}
                                </div>
                            </div>
                            <p className="text-[11px] leading-relaxed text-gray-500">{IMAGE_HINT}</p>
                            {/* 이미지 처리(배경제거·리사이즈)는 AI 보조작가 모달에서 이리로 옮겼다.
                                글 쓰기와 다른 일이라 이미지를 다루는 자리에 두는 것이 맞다. */}
                            {formData.thumbnail && (
                                <div className="flex flex-wrap items-center gap-2 pt-1">
                                    <span className="text-[11px] font-bold text-gray-500">이미지 처리</span>
                                    <button type="button" disabled={thumbProcessing} onClick={() => processThumbnail('removeBg')}
                                        className="px-2 py-1 rounded-md bg-white border border-gray-300 text-[11px] font-bold text-gray-700 hover:bg-gray-50 disabled:opacity-50">
                                        배경 제거
                                    </button>
                                    <button type="button" disabled={thumbProcessing} onClick={() => processThumbnail('resize')}
                                        className="px-2 py-1 rounded-md bg-white border border-gray-300 text-[11px] font-bold text-gray-700 hover:bg-gray-50 disabled:opacity-50">
                                        규격 맞추기
                                    </button>
                                    {thumbProcessing && <span className="text-[11px] text-gray-500">처리 중…</span>}
                                </div>
                            )}
                            {formData.thumbnail && (
                                <div className="mt-3 bg-white p-2 rounded-lg border border-gray-200 inline-block shadow-sm">
                                    <img src={formData.thumbnail} alt="Preview" className="h-20 object-contain rounded" />
                                </div>
                            )}
                        </div>
                        <div className="space-y-2">
                            <label className="block text-sm font-bold text-gray-700">제품 설명서 (PDF)</label>
                            <div className="flex gap-2 items-center">
                                <input type="file" onChange={(e) => handleFileUpload(e, 'manualFile')} className="block w-full text-xs text-gray-500 file:mr-2 file:py-1 file:px-3 file:rounded-md file:border-0 file:text-xs file:font-bold file:bg-white file:border-gray-200 file:text-gray-700 hover:file:bg-gray-50 cursor-pointer" accept={ACCEPT_PDF} />
                                <p className="mt-1 text-[11px] text-gray-500">{PDF_HINT}</p>
                                {formData.manualFile && (
                                    <button
                                        type="button"
                                        onClick={() => handleDeleteFile('manualFile')}
                                        className="w-8 h-8 flex items-center justify-center shrink-0 rounded-md bg-red-50 text-red-600 hover:bg-red-100 transition-colors shadow-sm font-bold"
                                        title="삭제"
                                    >
                                        ✕
                                    </button>
                                )}
                            </div>
                            {formData.manualFile && (
                                <div className="mt-2 text-xs font-medium text-blue-600 break-all">
                                    <a href={formData.manualFile} target="_blank" rel="noopener noreferrer" className="hover:underline flex items-center gap-1">📄 {formData.manualFile.split('/').pop()}</a>
                                </div>
                            )}
                        </div>
                        <div className="space-y-2">
                            <label className="block text-sm font-bold text-gray-700">제안서 (PDF)</label>
                            <div className="flex gap-2 items-center">
                                <input type="file" onChange={(e) => handleFileUpload(e, 'proposalFile')} className="block w-full text-xs text-gray-500 file:mr-2 file:py-1 file:px-3 file:rounded-md file:border-0 file:text-xs file:font-bold file:bg-white file:border-gray-200 file:text-gray-700 hover:file:bg-gray-50 cursor-pointer" accept={ACCEPT_PDF} />
                                <p className="mt-1 text-[11px] text-gray-500">{PDF_HINT}</p>
                                {formData.proposalFile && (
                                    <button
                                        type="button"
                                        onClick={() => handleDeleteFile('proposalFile')}
                                        className="w-8 h-8 flex items-center justify-center shrink-0 rounded-md bg-red-50 text-red-600 hover:bg-red-100 transition-colors shadow-sm font-bold"
                                        title="삭제"
                                    >
                                        ✕
                                    </button>
                                )}
                            </div>
                            {formData.proposalFile && (
                                <div className="mt-2 text-xs font-medium text-blue-600 break-all">
                                    <a href={formData.proposalFile} target="_blank" rel="noopener noreferrer" className="hover:underline flex items-center gap-1">📄 {formData.proposalFile.split('/').pop()}</a>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* 공개 여부 */}
                    <div className="space-y-2 flex items-center gap-3 bg-white p-4 rounded-xl border border-gray-200 shadow-sm w-max">
                        <label className="text-sm font-bold text-gray-700">공개 여부</label>
                        <label className="relative inline-flex items-center cursor-pointer">
                            <input
                                type="checkbox"
                                name="isPublic"
                                checked={formData.isPublic}
                                onChange={handleChange}
                                className="sr-only peer"
                            />
                            <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-500"></div>
                            <span className="ml-3 text-sm font-bold text-gray-600">{formData.isPublic ? '공개 중' : '비공개'}</span>
                        </label>
                    </div>

                    {/* 본문 에디터 */}
                    <div className="space-y-2">
                        <label className="block text-sm font-bold text-gray-700">본문 내용 *</label>
                        <div className="border border-gray-200 rounded-xl overflow-hidden shadow-sm">
                            <RichEditor
                                ref={editorRef}
                                height="600px"
                                placeholder="솔루션 상세 내용을 작성하세요..."
                            />
                        </div>
                    </div>

                    {/* 버튼 섹션 */}
                    <div className="flex gap-3 pt-6 border-t border-gray-100">
                        <AdminButton
                            type="button"
                            variant="secondary"
                            onClick={() => navigate('/admin/solutions')}
                            className="flex-1 max-w-[200px]"
                        >
                            취소
                        </AdminButton>
                        <AdminButton
                            type="submit"
                            variant="primary"
                            disabled={saving}
                            className="flex-1"
                        >
                            {saving ? '저장 중...' : (isEditMode ? '수정 사항 저장' : '새 솔루션 발행')}
                        </AdminButton>
                    </div>
                </form>
            </div>

            <AiAssistantModal
                isOpen={isAiModalOpen}
                onClose={() => setIsAiModalOpen(false)}
                contentType="solution"
                title="AI 보조작가 — 솔루션"
                fields={SOLUTION_AI_FIELDS}
                allowedCategories={categoryOptions.map((c) => c.label)}
                currentValues={{
                    title: formData.title,
                    subtitle: formData.subtitle,
                    category: categoryOptions.find((c) => c.id === formData.categoryIds[0])?.label || '',
                    content: editorRef.current?.getHTML() || '',
                }}
                onApply={handleAiApply}
            />

            <AiImageGeneratorModal
                isOpen={isAiImageModalOpen}
                onClose={() => setIsAiImageModalOpen(false)}
                onInsert={handleAiImageInsert}
            />

            <ContentCategoryManager
                isOpen={categoryManagerOpen}
                onClose={() => setCategoryManagerOpen(false)}
                contentType="solution"
                title="솔루션 카테고리 관리"
                onChanged={loadCategories}
            />
        </AdminLayout>
    );
};

export default SolutionEditor;